/* ===========================================================
   lib/router.js — real URLs, no page reloads

     /              your docs (or sign in)
     /d/<id>        a doc            /s/<token>  a share link
     /settings      /trash           /files      open from computer
     /local/<key>   a file from this computer
     /welcome       first-run question + tour
   Anything else is Proto's "lost" page.
   =========================================================== */

import { signal } from '@preact/signals';

function parse(loc = window.location) {
  const parts = loc.pathname.split('/').filter(Boolean).map(decodeURIComponent);
  const query = Object.fromEntries(new URLSearchParams(loc.search));
  return { path: loc.pathname, parts, query, hash: loc.hash.slice(1) };
}

export const route = signal(typeof window === 'undefined' ? { path: '/', parts: [], query: {} } : parse());

/** Things that must happen before leaving a page (e.g. save the doc). Return false to stay. */
const guards = new Set();
export function beforeLeave(fn) { guards.add(fn); return () => guards.delete(fn); }

export function go(path, { replace = false } = {}) {
  if (path === window.location.pathname + window.location.search) return;
  for (const g of guards) if (g() === false) return;
  window.history[replace ? 'replaceState' : 'pushState'](null, '', path);
  route.value = parse();
  window.scrollTo(0, 0);
}

export function startRouter() {
  window.addEventListener('popstate', () => { route.value = parse(); });
  // plain <a href="/…"> links navigate inside the app
  document.addEventListener('click', e => {
    const a = e.target.closest?.('a[href]');
    if (!a || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (a.target === '_blank' || a.hasAttribute('download')) return;
    const url = new URL(a.href, window.location.href);
    if (url.origin !== window.location.origin || url.pathname.startsWith('/api/') || url.pathname.startsWith('/f/')) return;
    e.preventDefault();
    go(url.pathname + url.search);
  });
}
